/**
 * /gratis-demo — Elidos premium demo-lead-sida.
 *
 * Ingen automatisk demo genereras. Besökaren skickar sin webbadress (eller
 * bekräftar att de saknar hemsida), fyller i grundläggande kontaktuppgifter
 * och begär en personlig kostnadsfri genomgång. Leadet:
 *   • sparas i backend (POST /api/leads/gratis-demo → contact_submissions)
 *   • mejlas till hej@elido.nu
 *   • bekräftelsemejl skickas till kunden
 *
 * Sidan stödjer `?industry=<slug>` för diskret personalisering.
 */
import { useState, useEffect, useMemo, useRef, forwardRef } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ArrowRight,
  Check,
  Loader2,
  Globe,
  ShieldCheck,
} from "lucide-react";
import { API_URL } from "@/utils/api";
import { captureAttribution, getAttribution } from "@/utils/attribution";
import { CONSENT_EVENTS, openSettings as openCookieSettings, getConsent, getConsentLeadFields } from "@/utils/cookieConsent";
import CookieConsent from "@/components/CookieConsent";

const LEAD_ENDPOINT = `${API_URL}/api/leads/gratis-demo`;
const CANONICAL = "https://elido.nu/gratis-demo";

// Godkända industry-slugs → visningsnamn i UI. Håll listan liten och
// verifierad. Okända värden ignoreras helt.
const INDUSTRY_LABELS = {
  vvs: "VVS",
  elektriker: "elektriker",
  malare: "målare",
  snickare: "snickare",
  bygg: "bygg",
  tak: "tak",
  golv: "golv",
  fonster: "fönster",
  stad: "städ",
  flytt: "flytt",
  trafallning: "trädfällning",
  dranering: "dränerings",
  hantverkare: "hantverkar",
  konsult: "konsult",
  klinik: "klinik",
  psykoterapi: "psykoterapi",
};

const GOALS = [
  { id: "more_requests", label: "Få fler förfrågningar" },
  { id: "modern_site", label: "Modernare hemsida" },
  { id: "google", label: "Synas bättre på Google" },
  { id: "explore", label: "Vet inte – visa möjligheterna" },
];

function normaliseUrl(v) {
  const t = (v || "").trim();
  if (!t) return "";
  if (/^https?:\/\//i.test(t)) return t;
  return `https://${t}`;
}

function isValidUrl(v) {
  const t = normaliseUrl(v);
  try {
    const u = new URL(t);
    return /^https?:$/.test(u.protocol) && u.hostname.includes(".");
  } catch {
    return false;
  }
}

function isValidEmail(v) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());
}

function track(name, extra = {}) {
  if (typeof window === "undefined") return;
  // dataLayer-events är analytics → kräver statistics-samtycke (Basic
  // Consent Mode). Köa inget utan samtycke.
  try {
    if (!getConsent().statistics) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: name, ...extra });
  } catch {
    /* aldrig krascha på tracking */
  }
}

export default function GratisDemoPage() {
  const [step, setStep] = useState(1);
  const [noWebsite, setNoWebsite] = useState(false);
  const [website, setWebsite] = useState("");
  const [industry, setIndustry] = useState("");

  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    goal: "",
    hp: "",
  });
  const [errors, setErrors] = useState({});
  const [state, setState] = useState("idle"); // idle|submitting|success|error
  const [errorMsg, setErrorMsg] = useState("");
  const [submitted, setSubmitted] = useState(null);
  const successRef = useRef(null);
  const step2Ref = useRef(null);

  // Läs & sanera ?industry=
  useEffect(() => {
    if (typeof window === "undefined") return;
    const q = new URLSearchParams(window.location.search).get("industry") || "";
    const slug = q.toLowerCase().replace(/[^a-z]/g, "").slice(0, 32);
    if (slug && INDUSTRY_LABELS[slug]) setIndustry(slug);
  }, []);

  useEffect(() => {
    captureAttribution();
    track("free_demo_started");
    // Consent Mode v2: om användaren accepterar marketing EFTER mount hoppar
    // captureAttribution() ut tomhänt vid initialkörningen. Kör därför om den
    // så snart CONSENT_EVENTS.UPDATED indikerar marketing=true — annars
    // hamnar leadet i direct_or_unattributed när URL:en fortfarande har
    // gclid/fbclid/utm_*. Samma mönster som /ny-hemsida.
    const onConsent = (e) => {
      if (e && e.detail && e.detail.marketing === true) {
        captureAttribution();
      }
    };
    window.addEventListener(CONSENT_EVENTS.UPDATED, onConsent);
    return () => window.removeEventListener(CONSENT_EVENTS.UPDATED, onConsent);
  }, []);

  const industryLabel = industry ? INDUSTRY_LABELS[industry] : "";
  const heroHeadline = useMemo(() => {
    if (industryLabel) return `Din nästa ${industryLabel}-hemsida börjar här.`;
    return "Din nästa hemsida börjar här.";
  }, [industryLabel]);

  function handleStep1(e) {
    e && e.preventDefault();
    setErrorMsg("");
    if (!noWebsite) {
      if (!website.trim() || !isValidUrl(website)) {
        setErrors({ website: "Ange en giltig webbadress (t.ex. www.dittforetag.se)." });
        return;
      }
    }
    setErrors({});
    setStep(2);
    track("free_demo_step_2", { no_website: noWebsite });
    setTimeout(() => step2Ref.current?.querySelector("input")?.focus(), 60);
  }

  function chooseNoWebsite() {
    setNoWebsite(true);
    setWebsite("");
    setErrors({});
    track("free_demo_no_website");
    setStep(2);
    setTimeout(() => step2Ref.current?.querySelector("input")?.focus(), 60);
  }

  function backToStep1() {
    setStep(1);
    setNoWebsite(false);
    setErrors({});
  }

  async function submit(e) {
    e.preventDefault();
    if (state === "submitting") return;
    setErrorMsg("");
    const next = {};
    if (!form.name.trim()) next.name = "Ange ditt namn.";
    if (!form.company.trim()) next.company = "Ange företagsnamn.";
    if (!form.email.trim() || !isValidEmail(form.email)) next.email = "Ange en giltig e-postadress.";
    if (Object.keys(next).length) {
      setErrors(next);
      return;
    }
    setErrors({});
    setState("submitting");
    try {
      const res = await fetch(LEAD_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          company: form.company.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          website: noWebsite ? "" : normaliseUrl(website),
          no_website: noWebsite,
          goal: form.goal,
          industry: industry || "",
          hp: form.hp,
          attribution: getAttribution(),
          ...getConsentLeadFields(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.accepted === true) {
        setSubmitted({
          company: form.company.trim(),
          website: noWebsite ? "" : normaliseUrl(website),
          goal: GOALS.find((g) => g.id === form.goal)?.label || "",
        });
        setState("success");
        track("free_demo_submitted", { lead_id: data.lead_id, no_website: noWebsite });
        setTimeout(() => successRef.current?.focus(), 40);
        return;
      }
      if (res.status === 422) {
        setErrorMsg(data.error || "Kontrollera formuläret och försök igen.");
      } else if (res.status === 429) {
        setErrorMsg(data.error || "Vänta en liten stund och försök igen.");
      } else {
        setErrorMsg(data.error || "Något gick fel. Försök igen om en stund.");
      }
      setState("error");
    } catch {
      setErrorMsg("Kunde inte nå servern. Kontrollera din anslutning.");
      setState("error");
    }
  }

  return (
    <>
      <Helmet>
        <title>Gratis hemsidedemo | Se hur vi skulle förbättra din hemsida | Elido</title>
        <meta
          name="description"
          content="Skicka din hemsida och låt Elido visa hur vi skulle förbättra den. Gratis personlig genomgång utan bindning."
        />
        <link rel="canonical" href={CANONICAL} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="sv_SE" />
        <meta
          property="og:title"
          content="Gratis hemsidedemo | Se hur vi skulle förbättra din hemsida | Elido"
        />
        <meta
          property="og:description"
          content="Skicka din hemsida och låt Elido visa hur vi skulle förbättra den. Gratis personlig genomgång utan bindning."
        />
        <meta property="og:url" content={CANONICAL} />
        <meta property="og:image" content="https://elido.nu/img/ny-hemsida/gratis-demo-og.jpg" />
        <meta property="og:image:secure_url" content="https://elido.nu/img/ny-hemsida/gratis-demo-og.jpg" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Elido — Din nästa hemsida börjar här. Gratis personlig demo." />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://elido.nu/img/ny-hemsida/gratis-demo-og.jpg" />
        <meta name="twitter:title" content="Gratis hemsidedemo | Se hur vi skulle förbättra din hemsida | Elido" />
        <meta name="twitter:description" content="Skicka din hemsida och låt Elido visa hur vi skulle förbättra den. Gratis personlig genomgång utan bindning." />
      </Helmet>

      <div
        className="min-h-screen bg-[#FBF7F2] text-[#0b1f27] font-['Inter',ui-sans-serif]"
        data-testid="gratis-demo-page"
      >
        {/* Avskalad header */}
        <header
          className="bg-white/70 backdrop-blur border-b border-slate-200/70"
          data-testid="gratis-demo-header"
        >
          <div className="max-w-6xl mx-auto px-5 lg:px-8 h-20 flex items-center justify-between">
            <Link
              to="/"
              className="flex items-center gap-2"
              data-testid="gratis-demo-logo"
              aria-label="Elido — startsida"
            >
              <img
                src="/img/ny-hemsida/elido-blue.png"
                alt="Elido"
                className="h-12 sm:h-14 w-auto"
                width="118"
                height="60"
              />
            </Link>
            <Link
              to="/kundcase"
              className="text-[13.5px] text-[#334155] hover:text-[#0b1f27] transition-colors hidden sm:inline"
              data-testid="gratis-demo-header-cases"
            >
              Kundcase
            </Link>
          </div>
        </header>

        {/* Hero + formulär */}
        <section
          className="relative overflow-hidden"
          style={{
            background:
              "radial-gradient(1000px 460px at 85% -10%, rgba(93,188,203,0.18) 0%, transparent 55%)," +
              "radial-gradient(900px 400px at -10% 100%, rgba(14,47,54,0.10) 0%, transparent 60%)",
          }}
        >
          <div className="max-w-4xl mx-auto px-5 lg:px-8 pt-12 lg:pt-20 pb-16 lg:pb-24 text-center">
            <div className="inline-flex items-center gap-2 text-[11px] font-semibold tracking-[0.16em] uppercase text-[#255E69] mb-6 px-3 py-1.5 rounded-full bg-white/60 border border-[#255E69]/15 backdrop-blur">
              Gratis personlig demo
            </div>
            <h1
              className="text-4xl sm:text-5xl lg:text-[3.4rem] font-bold leading-[1.05] tracking-tight"
              data-testid="gratis-demo-title"
            >
              {heroHeadline}
            </h1>
            <p className="mt-5 max-w-2xl mx-auto text-[16.5px] lg:text-[17.5px] text-[#334155] leading-relaxed">
              Skicka din webbadress så går vi igenom företaget och visar hur
              vi skulle förbättra er hemsida. Kostnadsfritt och utan förpliktelser.
            </p>

            {state === "success" ? (
              <SuccessCard ref={successRef} submitted={submitted} />
            ) : (
              <div className="mt-9">
                {step === 1 ? (
                  <Step1
                    website={website}
                    setWebsite={setWebsite}
                    onSubmit={handleStep1}
                    onNoWebsite={chooseNoWebsite}
                    error={errors.website}
                  />
                ) : (
                  <Step2
                    step2Ref={step2Ref}
                    form={form}
                    setForm={setForm}
                    errors={errors}
                    state={state}
                    errorMsg={errorMsg}
                    onSubmit={submit}
                    onBack={backToStep1}
                    noWebsite={noWebsite}
                    website={website}
                  />
                )}
              </div>
            )}
          </div>
        </section>

        {/* Så fungerar det */}
        <section className="bg-white py-16 lg:py-24" data-testid="how-it-works">
          <div className="max-w-5xl mx-auto px-5 lg:px-8">
            <div className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[#255E69] mb-3 text-center">
              Så fungerar det
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-center tracking-tight">
              Tre steg — ingenting krångligt
            </h2>
            <div className="mt-10 grid md:grid-cols-3 gap-5 lg:gap-6">
              {[
                {
                  n: "01",
                  t: "Skicka din hemsida",
                  d: "Eller berätta vilket företag du vill bygga en hemsida för.",
                },
                {
                  n: "02",
                  t: "Vi går igenom företaget",
                  d: "Vi tittar på nuläget, möjligheterna och vad som kan göras bättre.",
                },
                {
                  n: "03",
                  t: "Du får ett personligt förslag",
                  d: "Vi visar hur vi skulle ta nästa steg.",
                },
              ].map((s) => (
                <div
                  key={s.n}
                  className="rounded-2xl border border-slate-200 p-6 lg:p-7 bg-[#FBF7F2]"
                >
                  <div className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[#255E69] mb-2">
                    Steg {s.n}
                  </div>
                  <h3 className="text-lg font-bold text-[#0b1f27]">{s.t}</h3>
                  <p className="mt-2 text-[14.5px] text-[#334155] leading-relaxed">
                    {s.d}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Kort förtroende + varför gratis */}
        <section className="bg-[#FBF7F2] py-14 lg:py-20">
          <div className="max-w-4xl mx-auto px-5 lg:px-8 grid md:grid-cols-2 gap-8 lg:gap-10">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 lg:p-8">
              <h2 className="text-2xl font-bold text-[#0b1f27] tracking-tight">
                Vill du se vad vi redan byggt?
              </h2>
              <p className="mt-3 text-[15px] text-[#334155] leading-relaxed">
                Elido hjälper svenska företag med moderna hemsidor och en
                tydligare digital närvaro.
              </p>
              <Link
                to="/kundcase"
                className="mt-5 inline-flex items-center gap-2 text-[14px] font-semibold text-[#255E69] hover:text-[#0e2f36] transition-colors"
                data-testid="see-cases-link"
              >
                Se våra kundcase
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </Link>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-6 lg:p-8">
              <h2 className="text-2xl font-bold text-[#0b1f27] tracking-tight">
                Varför gör vi det gratis?
              </h2>
              <p className="mt-3 text-[15px] text-[#334155] leading-relaxed">
                Enklare att visa vad vi kan göra än att försöka förklara det i
                en lång säljpresentation.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-white py-14 lg:py-20" data-testid="faq">
          <div className="max-w-3xl mx-auto px-5 lg:px-8">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-center">
              Vanliga frågor
            </h2>
            <div className="mt-8 space-y-3">
              {[
                {
                  q: "Kostar det verkligen ingenting?",
                  a: "Ja. Förfrågan och vår första genomgång är kostnadsfri.",
                },
                { q: "Måste jag köpa något?", a: "Nej." },
                {
                  q: "Måste jag redan ha en hemsida?",
                  a: "Nej. Har du ingen hemsida kan vi istället titta på hur en ny skulle kunna byggas.",
                },
                {
                  q: "Vad händer efter att jag skickat?",
                  a: "Vi går igenom företaget och återkommer med ett personligt förslag.",
                },
              ].map((f, i) => (
                <details
                  key={i}
                  className="rounded-2xl border border-slate-200 bg-[#FBF7F2] px-5 py-4 group open:bg-white"
                >
                  <summary className="cursor-pointer text-[15.5px] font-semibold text-[#0b1f27] list-none flex justify-between items-center gap-4">
                    {f.q}
                    <span className="text-[#255E69] transition-transform group-open:rotate-45 text-xl leading-none">+</span>
                  </summary>
                  <p className="mt-3 text-[14.5px] text-[#334155] leading-relaxed">
                    {f.a}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Slut-CTA — visas bara när användaren fortfarande är på steg 1
             (annars konkurrerar det med det pågående formuläret). */}
        {state !== "success" && step === 1 && (
          <section
            className="bg-[#0e2f36] text-white py-14 lg:py-20"
            data-testid="final-cta"
          >
            <div className="max-w-3xl mx-auto px-5 lg:px-8 text-center">
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight leading-tight">
                Låt oss visa vad vi hade gjort annorlunda.
              </h2>
              <div className="mt-8">
                <Step1
                  website={website}
                  setWebsite={setWebsite}
                  onSubmit={(e) => {
                    handleStep1(e);
                    setTimeout(() => step2Ref.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
                    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 120);
                  }}
                  onNoWebsite={() => {
                    chooseNoWebsite();
                    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 60);
                  }}
                  error={errors.website}
                  dark
                />
              </div>
            </div>
          </section>
        )}

        <footer className="bg-[#0a1f24] text-white/60 py-6">
          <div className="max-w-6xl mx-auto px-5 lg:px-8 text-[12.5px] flex flex-wrap gap-3 justify-between items-center">
            <span>© {new Date().getFullYear()} Elido AB</span>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
              <Link to="/integritetspolicy" className="hover:text-white">
                Integritetspolicy
              </Link>
              <button
                type="button"
                onClick={openCookieSettings}
                className="hover:text-white bg-transparent border-0 p-0 cursor-pointer text-[12.5px]"
                data-testid="gratis-demo-cookie-settings"
              >
                Cookieinställningar
              </button>
            </div>
          </div>
        </footer>
        <CookieConsent />
      </div>
    </>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────

function Step1({ website, setWebsite, onSubmit, onNoWebsite, error, dark }) {
  const isDark = !!dark;
  return (
    <form onSubmit={onSubmit} className="max-w-xl mx-auto" data-testid="step-1">
      <div
        className={`flex flex-col sm:flex-row gap-2 p-1.5 rounded-2xl border shadow-[0_20px_50px_-24px_rgba(15,42,48,0.25)] ${
          isDark
            ? "bg-white/[0.06] border-white/20"
            : "bg-white border-slate-200"
        }`}
      >
        <div className="flex items-center gap-2 flex-1 px-3">
          <Globe className={`w-4 h-4 ${isDark ? "text-white/50" : "text-slate-400"}`} aria-hidden="true" />
          <input
            type="text"
            inputMode="url"
            autoComplete="url"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            placeholder="www.dittforetag.se"
            className={`flex-1 py-3 bg-transparent focus:outline-none text-[15.5px] ${
              isDark ? "text-white placeholder-white/40" : "text-[#0b1f27] placeholder-slate-400"
            }`}
            aria-label="Din webbadress"
            data-testid="step1-url-input"
          />
        </div>
        <button
          type="submit"
          className="inline-flex items-center justify-center gap-2 px-5 sm:px-6 py-3 rounded-xl font-semibold text-[15px] bg-[#F5A65B] hover:bg-[#eb9843] text-[#0b1f27] transition-all shadow-[0_10px_28px_-8px_rgba(245,166,91,0.55)]"
          data-testid="step1-submit"
        >
          Visa mig vad ni kan göra
          <ArrowRight className="w-4 h-4" aria-hidden="true" />
        </button>
      </div>
      {error && (
        <p className={`mt-2 text-[13px] ${isDark ? "text-red-300" : "text-red-600"}`} role="alert">
          {error}
        </p>
      )}
      <div className={`mt-3 flex flex-wrap gap-x-4 gap-y-1 justify-center text-[13px] ${isDark ? "text-white/70" : "text-[#475569]"}`}>
        <span>Gratis</span>
        <span aria-hidden="true">·</span>
        <span>Personligt</span>
        <span aria-hidden="true">·</span>
        <span>Ingen bindning</span>
      </div>
      <button
        type="button"
        onClick={onNoWebsite}
        className={`mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full border text-[14px] font-semibold transition-all duration-200 hover:translate-y-[-1px] ${
          isDark
            ? "border-white/25 text-white hover:bg-white/10 hover:border-white/40"
            : "border-[#255E69]/30 text-[#255E69] bg-white hover:bg-[#255E69]/5 hover:border-[#255E69]/55 shadow-[0_6px_18px_-10px_rgba(37,94,105,0.4)]"
        }`}
        data-testid="step1-no-website"
      >
        <span>Jag har ingen hemsida ännu</span>
        <ArrowRight className="w-4 h-4" aria-hidden="true" />
      </button>
    </form>
  );
}

function Step2({ step2Ref, form, setForm, errors, state, errorMsg, onSubmit, onBack, noWebsite, website }) {
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <form
      ref={step2Ref}
      onSubmit={onSubmit}
      className="mx-auto max-w-xl rounded-2xl bg-white border border-slate-200 p-5 sm:p-7 text-left shadow-[0_20px_50px_-24px_rgba(15,42,48,0.25)]"
      data-testid="step-2"
      noValidate
    >
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[#255E69]">
          Steg 2 av 2
        </div>
        <button
          type="button"
          onClick={onBack}
          className="text-[13px] text-[#475569] hover:text-[#0b1f27]"
          data-testid="step2-back"
        >
          ← Tillbaka
        </button>
      </div>

      {noWebsite ? (
        <p className="text-[13.5px] text-[#475569] mb-4">
          Ingen hemsida ännu — vi tittar istället på hur en ny skulle kunna byggas.
        </p>
      ) : (
        website && (
          <p className="text-[13.5px] text-[#475569] mb-4 break-all">
            Vi tittar på: <span className="font-semibold text-[#0b1f27]">{normaliseUrl(website)}</span>
          </p>
        )
      )}

      <Field id="gd-company" label="Företagsnamn" required value={form.company} onChange={set("company")} error={errors.company} autoComplete="organization" testId="form-company" />
      <Field id="gd-name" label="Ditt namn" required value={form.name} onChange={set("name")} error={errors.name} autoComplete="name" testId="form-name" />
      <Field id="gd-email" label="E-post" required type="email" value={form.email} onChange={set("email")} error={errors.email} autoComplete="email" testId="form-email" />
      <Field id="gd-phone" label="Telefon" optional type="tel" value={form.phone} onChange={set("phone")} autoComplete="tel" testId="form-phone" />

      <div className="mt-2">
        <label className="block text-[12px] font-semibold text-[#475569] mb-2 tracking-[0.08em] uppercase">
          Vad vill du framför allt förbättra?
        </label>
        <div className="grid gap-2">
          {GOALS.map((g) => (
            <label
              key={g.id}
              className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border cursor-pointer transition-colors text-[14.5px] ${
                form.goal === g.id
                  ? "border-[#255E69] bg-[#255E69]/5 text-[#0b1f27]"
                  : "border-slate-200 hover:border-slate-300 text-[#334155]"
              }`}
              data-testid={`form-goal-${g.id}`}
            >
              <input
                type="radio"
                name="goal"
                value={g.id}
                checked={form.goal === g.id}
                onChange={set("goal")}
                className="accent-[#255E69]"
              />
              <span>{g.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Honeypot */}
      <div aria-hidden="true" className="absolute -left-[9999px] w-px h-px overflow-hidden">
        <label htmlFor="gd-hp">Lämna tom</label>
        <input
          id="gd-hp"
          name="hp"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={form.hp}
          onChange={set("hp")}
        />
      </div>

      {errorMsg && (
        <div className="mt-4 text-[13.5px] text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2" role="alert">
          {errorMsg}
        </div>
      )}

      <button
        type="submit"
        disabled={state === "submitting"}
        className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-semibold text-[15px] bg-[#F5A65B] hover:bg-[#eb9843] text-[#0b1f27] disabled:opacity-60 disabled:pointer-events-none transition-all shadow-[0_10px_28px_-8px_rgba(245,166,91,0.55)]"
        data-testid="form-submit"
      >
        {state === "submitting" ? (
          <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
        ) : (
          <ArrowRight className="w-4 h-4" aria-hidden="true" />
        )}
        {state === "submitting" ? "Skickar…" : "Begär min gratis demo"}
      </button>

      <p className="mt-3 text-[12px] text-[#64748b] text-center flex items-center justify-center gap-1.5">
        <ShieldCheck className="w-3.5 h-3.5" aria-hidden="true" />
        Gratis · Personligt · Ingen bindning
      </p>
    </form>
  );
}

function Field({ id, label, required, optional, error, testId, ...rest }) {
  return (
    <div className="mb-3.5">
      <label htmlFor={id} className="block text-[12px] font-semibold text-[#475569] mb-1.5 tracking-[0.08em] uppercase">
        {label}
        {optional && (
          <span className="ml-2 normal-case tracking-normal text-[#94a3b8] font-normal">(frivilligt)</span>
        )}
      </label>
      <input
        id={id}
        required={!!required}
        className={`w-full px-4 py-2.5 rounded-xl bg-white border text-[15px] text-[#0b1f27] focus:outline-none focus:ring-2 focus:ring-[#255E69]/25 focus:border-[#255E69]/60 transition-colors ${
          error ? "border-red-400" : "border-slate-200"
        }`}
        data-testid={testId}
        {...rest}
      />
      {error && (
        <p
          className="mt-1 text-[12.5px] text-red-600"
          role="alert"
          data-testid={testId ? `${testId}-error` : undefined}
        >
          {error}
        </p>
      )}
    </div>
  );
}

const SuccessCard = forwardRef(function SuccessCard({ submitted }, ref) {
  return (
    <div
      ref={ref}
      tabIndex={-1}
      role="status"
      aria-live="polite"
      className="mt-10 rounded-2xl bg-white border border-slate-200 p-8 lg:p-10 max-w-xl mx-auto text-left focus:outline-none shadow-[0_20px_50px_-24px_rgba(15,42,48,0.25)]"
      data-testid="gratis-demo-success"
    >
      <div className="w-12 h-12 rounded-full bg-[#F5A65B]/15 border border-[#F5A65B]/40 flex items-center justify-center mb-4">
        <Check className="w-6 h-6 text-[#F5A65B]" aria-hidden="true" />
      </div>
      <h2 className="text-2xl font-bold text-[#0b1f27] leading-tight">
        Klart – vi har fått din förfrågan.
      </h2>
      <p className="mt-3 text-[15px] text-[#334155] leading-relaxed">
        Vi går igenom företaget och återkommer med ett personligt förslag.
        Du behöver inte göra något mer just nu.
      </p>

      {(submitted?.company || submitted?.website || submitted?.goal) && (
        <dl className="mt-6 grid gap-2 text-[13.5px]">
          {submitted.company && (
            <div className="flex gap-2">
              <dt className="text-[#64748b] w-32 shrink-0">Företag</dt>
              <dd className="text-[#0b1f27] font-medium break-all">{submitted.company}</dd>
            </div>
          )}
          {submitted.website && (
            <div className="flex gap-2">
              <dt className="text-[#64748b] w-32 shrink-0">Hemsida</dt>
              <dd className="text-[#0b1f27] font-medium break-all">{submitted.website}</dd>
            </div>
          )}
          {submitted.goal && (
            <div className="flex gap-2">
              <dt className="text-[#64748b] w-32 shrink-0">Vill förbättra</dt>
              <dd className="text-[#0b1f27] font-medium">{submitted.goal}</dd>
            </div>
          )}
        </dl>
      )}

      <Link
        to="/kundcase"
        className="mt-7 inline-flex items-center gap-2 px-5 py-3 rounded-xl font-semibold text-[14.5px] bg-[#0e2f36] hover:bg-[#164a55] text-white transition-colors"
        data-testid="success-see-cases"
      >
        Se våra kundcase
        <ArrowRight className="w-4 h-4" aria-hidden="true" />
      </Link>
    </div>
  );
});
